/**
 * AX vocabulary registries (RFC §7 AX-2/AX-3, §7a).
 *
 * Per-app registries of the STRUCTURAL vocabulary an agent reads from the DOM:
 * - state classes: `is-<state>` (kebab-case ASCII, registered). The namespaced
 *   escape hatch `is-x-<anything>` is ALWAYS legal for exploration (§7a) and
 *   therefore NOT registrable — registering it would defeat its purpose as
 *   visibly-unratified vocabulary.
 * - act verbs: kebab-case English matching command names (AX-3,
 *   `data-act="confirm-claim"` ↔ `evidence.confirm`). A verb may declare
 *   `payloadRequired` — the auditor then demands at least one other `data-*`
 *   attribute on the acting node.
 * - entity surfaces: tag names that MUST carry a `urn` attribute (AX-4).
 *
 * App vocabularies extend the built-in KIT vocabulary (web-kit's own
 * elements: kit-run-tree's `open-raw` drill verb) by default.
 */

const STATE_CLASS_RE = /^is-[a-z0-9]+(?:-[a-z0-9]+)*$/;
const KEBAB_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export interface ActVerbSpec {
  verb: string;
  /** When true, the auditor requires a data-* payload beside data-act. */
  payloadRequired?: boolean;
}

export interface AxVocabularyInput {
  /** The app's tag/vocabulary prefix, e.g. "career". */
  app: string;
  stateClasses?: readonly string[];
  actVerbs?: ReadonlyArray<string | ActVerbSpec>;
  /** Tag names that must carry a urn attribute (AX-4). */
  entitySurfaces?: readonly string[];
  /** Vocabularies to extend. Defaults to [KIT_VOCABULARY]. */
  extends?: readonly AxVocabulary[];
}

export interface AxVocabulary {
  /** All prefixes (own app + extended apps) — the AX-1 tag allowlist. */
  readonly apps: readonly string[];
  readonly stateClasses: ReadonlySet<string>;
  readonly actVerbs: ReadonlyMap<string, { payloadRequired: boolean }>;
  readonly entitySurfaces: ReadonlySet<string>;
}

export class AxVocabularyError extends Error {
  constructor(message: string) {
    super(`[web-kit] AX vocabulary: ${message}`);
    this.name = "AxVocabularyError";
  }
}

/** Build a validated per-app vocabulary. Throws `AxVocabularyError` on bad shapes. */
export function defineAxVocabulary(input: AxVocabularyInput): AxVocabulary {
  const { app } = input;
  if (!KEBAB_RE.test(app)) {
    throw new AxVocabularyError(
      `app prefix "${app}" must be kebab-case ASCII (e.g. "career").`
    );
  }

  const base = input.extends ?? [KIT_VOCABULARY];
  const apps = [app, ...base.flatMap(({ apps: a }) => a)];
  const stateClasses = new Set(base.flatMap(({ stateClasses: s }) => [...s]));
  const actVerbs = new Map(base.flatMap(({ actVerbs: v }) => [...v]));
  const entitySurfaces = new Set(
    base.flatMap(({ entitySurfaces: e }) => [...e])
  );

  for (const cls of input.stateClasses ?? []) {
    if (cls.startsWith("is-x-")) {
      throw new AxVocabularyError(
        `"${cls}" is in the is-x-* exploration namespace — it is always legal ` +
          `and cannot be registered (register it as "is-${cls.slice(5)}" once ratified).`
      );
    }
    if (!STATE_CLASS_RE.test(cls)) {
      throw new AxVocabularyError(
        `state class "${cls}" must match is-<state> (kebab-case ASCII, e.g. "is-accepted").`
      );
    }
    stateClasses.add(cls);
  }

  for (const entry of input.actVerbs ?? []) {
    const spec: ActVerbSpec =
      typeof entry === "string" ? { verb: entry } : entry;
    if (!KEBAB_RE.test(spec.verb)) {
      throw new AxVocabularyError(
        `act verb "${spec.verb}" must be kebab-case ASCII English (AX-3, e.g. "confirm-claim").`
      );
    }
    actVerbs.set(spec.verb, { payloadRequired: spec.payloadRequired ?? false });
  }

  for (const tag of input.entitySurfaces ?? []) {
    if (!KEBAB_RE.test(tag) || !tag.includes("-")) {
      throw new AxVocabularyError(
        `entity surface "${tag}" must be a custom-element tag name (kebab-case with a hyphen).`
      );
    }
    entitySurfaces.add(tag);
  }

  return { apps, stateClasses, actVerbs, entitySurfaces };
}

/**
 * The built-in KIT vocabulary — web-kit's own elements.
 * kit-run-tree (AX-8): the `open-raw` drill verb carries a `data-run` payload.
 * kit-scope-nav: `is-current` marks the active sibling inside a scope dropdown,
 * `is-leaf` the deepest breadcrumb — the page you are actually on.
 */
export const KIT_VOCABULARY: AxVocabulary = {
  apps: ["kit"],
  stateClasses: new Set<string>(["is-current", "is-leaf"]),
  actVerbs: new Map([["open-raw", { payloadRequired: true }]]),
  entitySurfaces: new Set<string>(),
};
